/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/12/22, 9:09 AM
 *
 */

import React from 'react';
import { Disclosure } from '@headlessui/react';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { CaretDown, WarningOctagon } from 'phosphor-react';

import { useMemberSubsDetails } from 'features/members/api/query/useMemberSubscription';

import Loader from 'components/Loader';
import SubscriptionChart from 'features/members/components/charts';
import { InvoiceHistory } from 'features/members/components/others/InvoiceHistory/InvoiceHistory';

interface ISubscriptionTab {
  member_id: number;
  role_id: number;
}

/**
 *
 * @param {number} role_id - id of role of selected member
 * @param {number} member_id - id of member in member list or member_id if it's there
 * @return {JSX.Element}
 */
function Tab({ member_id, role_id }: ISubscriptionTab) {
  const { isLoading, data: selectedMemberSubscription } = useMemberSubsDetails(member_id);

  const { start_date, end_date } = selectedMemberSubscription || {};

  if (isLoading && !selectedMemberSubscription) return <Loader />;

  return (
    <div className="flex items-start gap-4 md:flex-col">
      <div className="relative flex w-2/3 flex-col rounded-2xl bg-white p-8 shadow-sm md:w-full">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-3xl font-medium text-gray-800">Subscription Details</h1>
          </div>
          <div className="-mx-8 h-[0.4px] bg-gray-200" />
          {isEmpty(selectedMemberSubscription) || !selectedMemberSubscription ? (
            <div className="flex items-center space-x-4 text-error-500 print:hidden">
              <WarningOctagon size={40} />
              <span className="text-xl font-medium">
                No Subscription Found. Please contact Sunya Health Administrator
              </span>
            </div>
          ) : (
            <>
              <div className="mt-1">
                <div className="flex flex-col">
                  <div className="flex -translate-x-14 -translate-y-4 items-center  sm:translate-x-0 sm:flex-col ">
                    {start_date && end_date && (
                      <SubscriptionChart start_date={start_date} end_date={end_date} />
                    )}

                    <div className="flex flex-col space-y-4 ">
                      <h1 className="text-3xl font-medium text-gray-800">
                        {selectedMemberSubscription.plan.name}
                      </h1>
                      <div className="flex flex-col space-y-1">
                        <div className="flex gap-2 align-baseline">
                          <span className="text-xl font-medium text-gray-400">Duration:</span>
                          <span className="whitespace-nowrap text-xl font-medium text-gray-600">
                            {moment(selectedMemberSubscription.start_date * 1000).format(
                              'MMM Do, YYYY'
                            )}{' '}
                            -{' '}
                            {moment(selectedMemberSubscription.end_date * 1000).format(
                              'MMM Do, YYYY'
                            )}{' '}
                          </span>
                        </div>

                        <div className="flex gap-2 align-baseline">
                          <span className="text-xl font-medium text-gray-400">Paid Price:</span>
                          <span className="text-xl font-medium text-gray-600">
                            {selectedMemberSubscription?.plan.price}{' '}
                            {selectedMemberSubscription?.plan.currency}
                          </span>
                        </div>
                        <div className="flex gap-2 align-baseline">
                          <span className="text-xl font-medium text-gray-400">Grace Period:</span>
                          <span className="text-xl font-medium text-gray-600">
                            {selectedMemberSubscription.plan.grace_period} days
                          </span>
                        </div>
                        <div className="flex gap-2 align-baseline">
                          <span className="text-xl font-medium text-gray-400">Test Limits:</span>
                          <span className="text-xl font-medium text-gray-600">
                            {selectedMemberSubscription.plan.test_limit} times
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="-mx-4 h-[0.4px] bg-slate-200 " />

                  <div className="flex flex-col space-y-4 py-8">
                    <h1 className="text-2xl font-medium text-gray-800">
                      Tests Included In This Subscription
                    </h1>
                    <div className="grid grid-cols-2 gap-4 text-lg">
                      {selectedMemberSubscription?.plan.test_categories.map((category) => (
                        <div
                          className="flex w-full flex-col items-start self-start rounded-lg bg-gray-100 py-2 px-6 text-lg sm:w-full sm:items-center"
                          key={category.id}
                        >
                          <Disclosure>
                            <Disclosure.Button className="flex w-full items-center justify-between py-2 text-xl font-medium capitalize tracking-wider text-gray-800">
                              {category.name}
                              <span>
                                <CaretDown />
                              </span>
                            </Disclosure.Button>
                            <Disclosure.Panel>
                              <div className="flex flex-wrap gap-x-4 gap-y-2">
                                {category.sub_categories.map((sub_category) => (
                                  <span
                                    key={sub_category.id}
                                    className="text-lg font-medium capitalize text-gray-500 text-gray-500 line-clamp-1  "
                                  >
                                    {sub_category.name}
                                  </span>
                                ))}
                              </div>
                            </Disclosure.Panel>
                          </Disclosure>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="-mx-4 h-[0.4px] bg-slate-200 " />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="w-1/3 flex-col md:w-full">
        <div className="relative flex flex-col rounded-2xl bg-white p-8 shadow-sm">
          <InvoiceHistory member_id={member_id} />
        </div>
      </div>
    </div>
  );
}

export const SubscriptionTab = React.memo(Tab);
